/**
 * Verify Email Page
 *
 * Shown when a user signs in or signs up with an unverified email address.
 * Prevents the infinite redirect loop that occurs when an unverified Firebase
 * session briefly sets `user` in AuthContext before sign-out completes.
 *
 * Email address is passed via router location state (`state.email`) so the
 * page can show it even after the Firebase session has been signed out.
 */

import { type JSX, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '~/hooks/useAuth';

import { Logo } from '~/components/Logo';

interface LocationState {
  email?: string;
  password?: string;
}

export function VerifyEmail(): JSX.Element {
  const location = useLocation();
  const state = (location.state as LocationState) ?? {};
  const email = state.email ?? '';
  const password = state.password ?? '';

  const { resendEmailVerification } = useAuth();
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [error, setError] = useState('');

  const handleResend = (): void => {
    if (!email || !password) return;
    setResendLoading(true);
    setResendSent(false);
    setError('');
    const run = async (): Promise<void> => {
      try {
        await resendEmailVerification(email, password);
        setResendSent(true);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to resend. Please try signing in again.',
        );
      } finally {
        setResendLoading(false);
      }
    };
    void run();
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-background text-foreground p-8">
      <Logo />

      <div className="mt-[20vh] w-full max-w-sm space-y-6 text-center">
        <div className="space-y-2">
          <div className="text-4xl">✉️</div>
          <h1 className="text-2xl font-semibold">Check your inbox</h1>
          {email ? (
            <p className="text-sm text-foreground/60">
              We sent a verification link to{' '}
              <span className="font-medium text-foreground">{email}</span>. Click the link, then
              come back here to sign in.
            </p>
          ) : (
            <p className="text-sm text-foreground/60">
              We sent a verification link to your email address. Click the link, then come back here
              to sign in.
            </p>
          )}
        </div>

        <div className="space-y-3">
          {resendSent ? (
            <p className="text-sm text-success font-medium">&#10003; Verification email sent</p>
          ) : (
            password && (
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading}
                className="w-full py-2 px-4 rounded text-sm font-medium border border-default hover:bg-default/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resendLoading ? 'Sending\u2026' : 'Resend verification email'}
              </button>
            )
          )}

          {error && <p className="text-sm text-danger">{error}</p>}

          <Link
            to="/login"
            className="block w-full py-2 px-4 rounded text-sm font-medium text-center text-foreground/70 hover:text-foreground hover:bg-default/10 transition-colors"
          >
            &larr; Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
